import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Target, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Loader2,
  Upload,
  FileText,
  Users,
  DollarSign,
  Shield,
  Briefcase,
  X,
  User,
  Home,
  Phone,
  Mail,
  Calendar,
  AlertCircle
} from "lucide-react";
import { useAuth } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_PURPOSES = [
  { id: "business_expansion", name: "Business Expansion", description: "Expand your existing business operations" },
  { id: "working_capital", name: "Working Capital", description: "Day-to-day business operations funding" },
  { id: "equipment_purchase", name: "Equipment Purchase", description: "Buy equipment or machinery" },
  { id: "inventory", name: "Inventory Purchase", description: "Stock up on inventory" },
  { id: "education", name: "Education", description: "Educational expenses" },
  { id: "agriculture", name: "Agriculture", description: "Farming and agricultural activities" },
  { id: "other", name: "Other Productive Purpose", description: "Other income-generating activities" }
];

const DOCUMENT_CATEGORIES = [
  { id: "national_id", name: "National ID (Both Sides)", required: true },
  { id: "proof_of_residence", name: "Proof of Residence", required: true },
  { id: "payslip", name: "Latest Payslip / Proof of Income", required: true },
  { id: "bank_statement", name: "Bank Statement (3 months)", required: true },
  { id: "employment_letter", name: "Employment Confirmation Letter", required: false },
  { id: "business_proof", name: "Business Registration / Proof of Business", required: false },
  { id: "other", name: "Other Supporting Documents", required: false }
];

const IndividualLoanApplication = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const [formData, setFormData] = useState({
    // Personal Data
    surname: "",
    first_name: "",
    physical_address: "",
    property_ownership: "",
    id_number: "",
    gender: "",
    date_of_birth: "",
    phone: "",
    email: "",
    marital_status: "",
    spouse_name: "",
    spouse_phone: "",
    spouse_occupation: "",
    number_of_children: "",
    total_dependants: "",
    dependants_at_school: "",
    adult_dependants: "",
    
    // Next of Kin
    next_of_kin_name: "",
    next_of_kin_relationship: "",
    next_of_kin_phone: "",
    next_of_kin_address: "",
    
    // Loan Details (Sol Prop Loans)
    loan_product: "sol_prop",
    loan_purpose: "",
    purpose_description: "",
    amount_required: "",
    tenure_months: "",
    custom_tenure: "",
    own_capital: "",
    expected_income: "",
    affordable_instalment: "yes",
    
    // For Remittance Loans
    remittance_source_country: "",
    remittance_provider: "",
    expected_remittance_amount: "",
    remittance_frequency: "",
    
    // Financial Information
    gross_income: "",
    net_income: "",
    other_income: "",
    spouse_gross_income: "",
    spouse_net_income: "",
    monthly_expenses: "",
    
    // Employment
    employment_status: "",
    is_self_employed: "no",
    business_name: "",
    years_in_business: "",
    occupation: "",
    current_employer: "",
    years_with_employer: "",
    previous_employer: "",
    years_with_previous: "",
    
    // Existing Loans
    has_existing_loans: "no",
    existing_loans: [{
      lender: "",
      loan_type: "",
      loan_amount: "",
      current_balance: "",
      tenure: "",
      monthly_repayment: ""
    }],
    
    // Referees
    referees: [{
      name: "",
      relationship: "",
      phone: "",
      address: ""
    }],
    
    // Assets/Collateral
    assets: [{
      description: "",
      serial_number: "",
      market_value: ""
    }],
    
    // Consent
    consent_identity_verification: false,
    consent_credit_bureau: false,
    
    // Declaration
    declaration_agreed: false,
    application_fee: ""
  });

  const totalSteps = 9; // Added review step
  const progress = (step / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleExistingLoanChange = (index, field, value) => {
    setFormData(prev => {
      const loans = [...prev.existing_loans];
      loans[index] = { ...loans[index], [field]: value };
      return { ...prev, existing_loans: loans };
    });
  };

  const addExistingLoan = () => {
    setFormData(prev => ({
      ...prev,
      existing_loans: [...prev.existing_loans, {
        lender: "", loan_type: "", loan_amount: "", current_balance: "", tenure: "", monthly_repayment: ""
      }]
    }));
  };

  const handleRefereeChange = (index, field, value) => {
    setFormData(prev => {
      const refs = [...prev.referees];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, referees: refs };
    });
  };

  const addReferee = () => {
    setFormData(prev => ({
      ...prev,
      referees: [...prev.referees, { name: "", relationship: "", phone: "", address: "" }]
    }));
  };

  const handleAssetChange = (index, field, value) => {
    setFormData(prev => {
      const assets = [...prev.assets];
      assets[index] = { ...assets[index], [field]: value };
      return { ...prev, assets: assets };
    });
  };

  const addAsset = () => {
    setFormData(prev => ({
      ...prev,
      assets: [...prev.assets, { description: "", serial_number: "", market_value: "" }]
    }));
  };

  const handleFileUpload = async (categoryId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadingDoc(categoryId);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocuments(prev => ({
          ...prev,
          [categoryId]: {
            name: file.name,
            size: file.size,
            type: file.type,
            data: reader.result,
            uploadedAt: new Date().toISOString()
          }
        }));
        toast.success(`${file.name} uploaded`);
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Upload failed");
      setUploadingDoc(null);
    }
  };

  const removeDocument = (categoryId) => {
    setDocuments(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.surname && formData.first_name && formData.id_number && formData.phone;
      case 2:
        return formData.loan_purpose && formData.amount_required && (formData.tenure_months || formData.custom_tenure);
      case 3:
        return formData.gross_income && formData.net_income;
      case 4:
        return formData.employment_status;
      case 5:
        return true; // Existing loans optional
      case 6:
        return formData.referees[0].name && formData.referees[0].phone;
      case 7:
        return Object.keys(documents).length >= 3;
      case 8:
        return true; // Review step - no validation needed
      case 9:
        return formData.declaration_agreed && formData.consent_identity_verification && formData.consent_credit_bureau;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.declaration_agreed) {
      toast.error("Please agree to the declarations");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        applicant_type: "individual",
        loan_type: "sol_prop",
        loan_product: "Sol Prop Loans",
        full_name: `${formData.first_name} ${formData.surname}`,
        amount: parseFloat(formData.amount_required) || 0,
        term_months: parseInt(formData.tenure_months) || 12,
        documents_meta: Object.keys(documents).reduce((acc, key) => {
          acc[key] = {
            name: documents[key].name,
            size: documents[key].size,
            type: documents[key].type,
            uploadedAt: documents[key].uploadedAt
          };
          return acc;
        }, {})
      };

      const response = await axios.post(
        `${API}/individual-loans/apply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const appId = response.data.application_id;
      setApplicationId(appId);

      if (Object.keys(documents).length > 0) {
        try {
          await axios.post(
            `${API}/documents/upload`,
            { application_id: appId, documents },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (docError) {
          console.error("Document upload error:", docError);
        }
      }

      toast.success("Application submitted successfully!");
      setStep(totalSteps + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Personal Information";
      case 2: return "Loan Details";
      case 3: return "Financial Information";
      case 4: return "Employment Details";
      case 5: return "Existing Obligations";
      case 6: return "Referees & Security";
      case 7: return "Supporting Documents";
      case 8: return "Review Application";
      case 9: return "Consent & Declaration";
      default: return "Application Complete";
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 1: return User;
      case 2: return Target;
      case 3: return DollarSign;
      case 4: return Briefcase;
      case 5: return FileText;
      case 6: return Users;
      case 7: return Upload;
      case 8: return FileText;
      case 9: return CheckCircle;
      default: return CheckCircle;
    }
  };

  const StepIcon = getStepIcon();

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Surname *</Label>
                <Input
                  value={formData.surname}
                  onChange={(e) => handleInputChange("surname", e.target.value)}
                  placeholder="Enter surname"
                />
              </div>
              <div>
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleInputChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <Label>National ID Number *</Label>
                <Input
                  value={formData.id_number}
                  onChange={(e) => handleInputChange("id_number", e.target.value)}
                  placeholder="e.g., 12-345678-A90"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={formData.gender} onValueChange={(v) => handleInputChange("gender", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange("date_of_birth", e.target.value)}
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="+263..."
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label>Marital Status</Label>
                <Select value={formData.marital_status} onValueChange={(v) => handleInputChange("marital_status", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Physical Address (Residence)</Label>
                <Textarea
                  value={formData.physical_address}
                  onChange={(e) => handleInputChange("physical_address", e.target.value)}
                  placeholder="Enter your residential address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Property Ownership</Label>
                <div className="flex gap-4 mt-2">
                  {["owned", "rented"].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="property_ownership"
                        value={opt}
                        checked={formData.property_ownership === opt}
                        onChange={(e) => handleInputChange("property_ownership", e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="capitalize">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {formData.marital_status === "married" && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Spouse Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Spouse Full Name</Label>
                    <Input
                      value={formData.spouse_name}
                      onChange={(e) => handleInputChange("spouse_name", e.target.value)}
                      placeholder="Spouse name"
                    />
                  </div>
                  <div>
                    <Label>Spouse Phone</Label>
                    <Input
                      value={formData.spouse_phone}
                      onChange={(e) => handleInputChange("spouse_phone", e.target.value)}
                      placeholder="+263..."
                    />
                  </div>
                  <div>
                    <Label>Spouse Occupation & Employer</Label>
                    <Input
                      value={formData.spouse_occupation}
                      onChange={(e) => handleInputChange("spouse_occupation", e.target.value)}
                      placeholder="e.g., Teacher at ABC School"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Next of Kin</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={formData.next_of_kin_name}
                    onChange={(e) => handleInputChange("next_of_kin_name", e.target.value)}
                    placeholder="Next of kin name"
                  />
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Input
                    value={formData.next_of_kin_relationship}
                    onChange={(e) => handleInputChange("next_of_kin_relationship", e.target.value)}
                    placeholder="e.g., Brother, Parent"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.next_of_kin_phone}
                    onChange={(e) => handleInputChange("next_of_kin_phone", e.target.value)}
                    placeholder="+263..."
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.next_of_kin_address}
                    onChange={(e) => handleInputChange("next_of_kin_address", e.target.value)}
                    placeholder="Next of kin address"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Dependants</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>No. of Children</Label>
                  <Input
                    type="number"
                    value={formData.number_of_children}
                    onChange={(e) => handleInputChange("number_of_children", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Total Dependants</Label>
                  <Input
                    type="number"
                    value={formData.total_dependants}
                    onChange={(e) => handleInputChange("total_dependants", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>At School</Label>
                  <Input
                    type="number"
                    value={formData.dependants_at_school}
                    onChange={(e) => handleInputChange("dependants_at_school", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Adult Dependants</Label>
                  <Input
                    type="number"
                    value={formData.adult_dependants}
                    onChange={(e) => handleInputChange("adult_dependants", e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label>Loan Purpose *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {LOAN_PURPOSES.map(purpose => (
                  <div
                    key={purpose.id}
                    onClick={() => handleInputChange("loan_purpose", purpose.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.loan_purpose === purpose.id
                        ? 'border-[#0A2540] bg-[#0A2540]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-sm">{purpose.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{purpose.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <Label>Describe your productive purpose</Label>
              <Textarea
                value={formData.purpose_description}
                onChange={(e) => handleInputChange("purpose_description", e.target.value)}
                placeholder="Explain how you plan to use the loan funds for productive purposes..."
                rows={3}
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Loan Amount & Terms</h4>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Sol Prop Loans:</strong> 6-10% monthly rate • 1-12 months term • $100 - $10,000
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Amount Required (USD) *</Label>
                  <Input
                    type="number"
                    value={formData.amount_required}
                    onChange={(e) => handleInputChange("amount_required", e.target.value)}
                    placeholder="Enter amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">$100 - $10,000</p>
                </div>
                <div>
                  <Label>Tenure (months) *</Label>
                  <Select value={formData.tenure_months} onValueChange={(v) => {
                    handleInputChange("tenure_months", v);
                    if (v !== "custom") handleInputChange("custom_tenure", "");
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tenure" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 month</SelectItem>
                      <SelectItem value="2">2 months</SelectItem>
                      <SelectItem value="3">3 months</SelectItem>
                      <SelectItem value="4">4 months</SelectItem>
                      <SelectItem value="5">5 months</SelectItem>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="7">7 months</SelectItem>
                      <SelectItem value="8">8 months</SelectItem>
                      <SelectItem value="9">9 months</SelectItem>
                      <SelectItem value="10">10 months</SelectItem>
                      <SelectItem value="11">11 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="custom">Custom tenure</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.tenure_months === "custom" && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        value={formData.custom_tenure}
                        onChange={(e) => handleInputChange("custom_tenure", e.target.value)}
                        placeholder="Enter number of months"
                        min="1"
                        max="60"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter 1-60 months</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Own Capital / Deposit (USD)</Label>
                  <Input
                    type="number"
                    value={formData.own_capital}
                    onChange={(e) => handleInputChange("own_capital", e.target.value)}
                    placeholder="Amount you can contribute"
                  />
                </div>
                <div>
                  <Label>Expected Monthly Income from Activity (USD)</Label>
                  <Input
                    type="number"
                    value={formData.expected_income}
                    onChange={(e) => handleInputChange("expected_income", e.target.value)}
                    placeholder="Projected income"
                  />
                </div>
                <div>
                  <Label>Affordable Monthly Instalment?</Label>
                  <div className="flex gap-4 mt-2">
                    {["yes", "no"].map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="affordable_instalment"
                          value={opt}
                          checked={formData.affordable_instalment === opt}
                          onChange={(e) => handleInputChange("affordable_instalment", e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="capitalize">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Gross Monthly Income (USD) *</Label>
                <Input
                  type="number"
                  value={formData.gross_income}
                  onChange={(e) => handleInputChange("gross_income", e.target.value)}
                  placeholder="Before deductions"
                />
              </div>
              <div>
                <Label>Net Monthly Income (USD) *</Label>
                <Input
                  type="number"
                  value={formData.net_income}
                  onChange={(e) => handleInputChange("net_income", e.target.value)}
                  placeholder="Take-home pay"
                />
              </div>
              <div>
                <Label>Other Income (USD)</Label>
                <Input
                  type="number"
                  value={formData.other_income}
                  onChange={(e) => handleInputChange("other_income", e.target.value)}
                  placeholder="Side income, rentals, etc."
                />
              </div>
              <div>
                <Label>Monthly Expenses (USD)</Label>
                <Input
                  type="number"
                  value={formData.monthly_expenses}
                  onChange={(e) => handleInputChange("monthly_expenses", e.target.value)}
                  placeholder="Total monthly expenses"
                />
              </div>
            </div>

            {formData.marital_status === "married" && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Spouse Income</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Spouse Gross Income (USD)</Label>
                    <Input
                      type="number"
                      value={formData.spouse_gross_income}
                      onChange={(e) => handleInputChange("spouse_gross_income", e.target.value)}
                      placeholder="Spouse gross income"
                    />
                  </div>
                  <div>
                    <Label>Spouse Net Income (USD)</Label>
                    <Input
                      type="number"
                      value={formData.spouse_net_income}
                      onChange={(e) => handleInputChange("spouse_net_income", e.target.value)}
                      placeholder="Spouse net income"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label>Employment Status *</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {["permanent", "temporary", "contract", "unemployed"].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="employment_status"
                      value={opt}
                      checked={formData.employment_status === opt}
                      onChange={(e) => handleInputChange("employment_status", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="capitalize">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Are you Self-Employed?</Label>
              <div className="flex gap-4 mt-2">
                {["yes", "no"].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_self_employed"
                      value={opt}
                      checked={formData.is_self_employed === opt}
                      onChange={(e) => handleInputChange("is_self_employed", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="capitalize">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.is_self_employed === "yes" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label>Business Name</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => handleInputChange("business_name", e.target.value)}
                    placeholder="Your business name"
                  />
                </div>
                <div>
                  <Label>Years in Business</Label>
                  <Input
                    type="number"
                    value={formData.years_in_business}
                    onChange={(e) => handleInputChange("years_in_business", e.target.value)}
                    placeholder="e.g., 3"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Occupation / Profession</Label>
                <Input
                  value={formData.occupation}
                  onChange={(e) => handleInputChange("occupation", e.target.value)}
                  placeholder="e.g., Accountant, Teacher"
                />
              </div>
              <div>
                <Label>Current Employer</Label>
                <Input
                  value={formData.current_employer}
                  onChange={(e) => handleInputChange("current_employer", e.target.value)}
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label>Years with Current Employer</Label>
                <Input
                  type="number"
                  value={formData.years_with_employer}
                  onChange={(e) => handleInputChange("years_with_employer", e.target.value)}
                  placeholder="e.g., 2"
                />
              </div>
              <div>
                <Label>Previous Employer</Label>
                <Input
                  value={formData.previous_employer}
                  onChange={(e) => handleInputChange("previous_employer", e.target.value)}
                  placeholder="Previous company"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <Label>Do you have any existing loans?</Label>
              <div className="flex gap-4 mt-2">
                {["yes", "no"].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_existing_loans"
                      value={opt}
                      checked={formData.has_existing_loans === opt}
                      onChange={(e) => handleInputChange("has_existing_loans", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="capitalize">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.has_existing_loans === "yes" && (
              <div className="space-y-4">
                {formData.existing_loans.map((loan, index) => (
                  <Card key={index} className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Existing Loan {index + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Lender</Label>
                        <Input
                          value={loan.lender}
                          onChange={(e) => handleExistingLoanChange(index, "lender", e.target.value)}
                          placeholder="Lender name"
                        />
                      </div>
                      <div>
                        <Label>Type of Loan</Label>
                        <Input
                          value={loan.loan_type}
                          onChange={(e) => handleExistingLoanChange(index, "loan_type", e.target.value)}
                          placeholder="e.g., Personal, Device"
                        />
                      </div>
                      <div>
                        <Label>Loan Amount (USD)</Label>
                        <Input
                          type="number"
                          value={loan.loan_amount}
                          onChange={(e) => handleExistingLoanChange(index, "loan_amount", e.target.value)}
                          placeholder="Original amount"
                        />
                      </div>
                      <div>
                        <Label>Current Balance (USD)</Label>
                        <Input
                          type="number"
                          value={loan.current_balance}
                          onChange={(e) => handleExistingLoanChange(index, "current_balance", e.target.value)}
                          placeholder="Outstanding"
                        />
                      </div>
                      <div>
                        <Label>Tenure</Label>
                        <Input
                          value={loan.tenure}
                          onChange={(e) => handleExistingLoanChange(index, "tenure", e.target.value)}
                          placeholder="e.g., 12 months"
                        />
                      </div>
                      <div>
                        <Label>Monthly Repayment (USD)</Label>
                        <Input
                          type="number"
                          value={loan.monthly_repayment}
                          onChange={(e) => handleExistingLoanChange(index, "monthly_repayment", e.target.value)}
                          placeholder="Monthly payment"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addExistingLoan}>
                  + Add Another Loan
                </Button>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                By providing these references, you authorize Tzur Finances to contact them.
              </p>
              
              {formData.referees.map((ref, index) => (
                <Card key={index} className="bg-gray-50 mb-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Referee {index + 1} {index === 0 && "*"}</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name {index === 0 && "*"}</Label>
                      <Input
                        value={ref.name}
                        onChange={(e) => handleRefereeChange(index, "name", e.target.value)}
                        placeholder="Referee name"
                      />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input
                        value={ref.relationship}
                        onChange={(e) => handleRefereeChange(index, "relationship", e.target.value)}
                        placeholder="e.g., Colleague, Friend"
                      />
                    </div>
                    <div>
                      <Label>Phone Number {index === 0 && "*"}</Label>
                      <Input
                        value={ref.phone}
                        onChange={(e) => handleRefereeChange(index, "phone", e.target.value)}
                        placeholder="+263..."
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={ref.address}
                        onChange={(e) => handleRefereeChange(index, "address", e.target.value)}
                        placeholder="Physical address"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addReferee}>
                + Add Another Referee
              </Button>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium mb-2">Personal Assets as Security</h4>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Assets must be worth at least 2x the loan amount and cannot be disposed of before the loan is paid off.
                  </p>
                </div>
              </div>
              
              {formData.assets.map((asset, index) => (
                <Card key={index} className="bg-gray-50 mb-4">
                  <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Asset Description</Label>
                      <Input
                        value={asset.description}
                        onChange={(e) => handleAssetChange(index, "description", e.target.value)}
                        placeholder="e.g., Samsung TV 55 inch"
                      />
                    </div>
                    <div>
                      <Label>Serial Number</Label>
                      <Input
                        value={asset.serial_number}
                        onChange={(e) => handleAssetChange(index, "serial_number", e.target.value)}
                        placeholder="Serial/IMEI number"
                      />
                    </div>
                    <div>
                      <Label>Market Value (USD)</Label>
                      <Input
                        type="number"
                        value={asset.market_value}
                        onChange={(e) => handleAssetChange(index, "market_value", e.target.value)}
                        placeholder="Current value"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addAsset}>
                + Add Another Asset
              </Button>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Upload required documents. Accepted formats: PDF, Images (max 10MB each).
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {DOCUMENT_CATEGORIES.map((category) => (
                <div 
                  key={category.id}
                  className={`border rounded-lg p-4 ${
                    documents[category.id] ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className={`h-5 w-5 ${documents[category.id] ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-sm">
                          {category.name}
                          {category.required && <span className="text-red-500 ml-1">*</span>}
                        </p>
                        {documents[category.id] && (
                          <p className="text-xs text-emerald-600">
                            {documents[category.id].name} ({formatFileSize(documents[category.id].size)})
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {documents[category.id] ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeDocument(category.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(category.id, e.target.files?.[0])}
                          disabled={uploadingDoc === category.id}
                        />
                        <Button variant="outline" size="sm" className="pointer-events-none">
                          {uploadingDoc === category.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload
                            </>
                          )}
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                Documents Uploaded: {Object.keys(documents).length} / {DOCUMENT_CATEGORIES.length}
              </p>
            </div>
          </div>
        );

      case 8:
        // Review Step
        const getTenureDisplay = () => {
          if (formData.tenure_months === "custom") return `${formData.custom_tenure} months (custom)`;
          return `${formData.tenure_months} months`;
        };
        
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Please review your application carefully before proceeding to submit.</strong>
              </p>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Full Name:</span> <span className="font-medium">{formData.first_name} {formData.surname}</span></div>
                <div><span className="text-gray-500">ID Number:</span> <span className="font-medium">{formData.id_number}</span></div>
                <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{formData.phone}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="font-medium">{formData.email || "Not provided"}</span></div>
                <div><span className="text-gray-500">Address:</span> <span className="font-medium">{formData.physical_address || "Not provided"}</span></div>
                <div><span className="text-gray-500">Gender:</span> <span className="font-medium capitalize">{formData.gender || "Not provided"}</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Loan Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Loan Product:</span> <span className="font-medium">{formData.loan_product === "remittance" ? "Remittance Loan" : "Sol Prop Loan"}</span></div>
                <div><span className="text-gray-500">Purpose:</span> <span className="font-medium capitalize">{formData.loan_purpose?.replace(/_/g, " ")}</span></div>
                <div><span className="text-gray-500">Amount Requested:</span> <span className="font-medium">${formData.amount_required}</span></div>
                <div><span className="text-gray-500">Tenure:</span> <span className="font-medium">{getTenureDisplay()}</span></div>
                <div><span className="text-gray-500">Own Capital:</span> <span className="font-medium">${formData.own_capital || "0"}</span></div>
                <div><span className="text-gray-500">Expected Income:</span> <span className="font-medium">${formData.expected_income || "0"}/month</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Financial & Employment</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Gross Income:</span> <span className="font-medium">${formData.gross_income}/month</span></div>
                <div><span className="text-gray-500">Net Income:</span> <span className="font-medium">${formData.net_income}/month</span></div>
                <div><span className="text-gray-500">Employment Status:</span> <span className="font-medium capitalize">{formData.employment_status?.replace(/_/g, " ")}</span></div>
                <div><span className="text-gray-500">Employer/Business:</span> <span className="font-medium">{formData.current_employer || formData.business_name || "Not provided"}</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Documents & Referees</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <div className="mb-2"><span className="text-gray-500">Documents Uploaded:</span> <span className="font-medium">{Object.keys(documents).length} documents</span></div>
                <div><span className="text-gray-500">Primary Referee:</span> <span className="font-medium">{formData.referees[0]?.name || "Not provided"} ({formData.referees[0]?.phone || "N/A"})</span></div>
              </CardContent>
            </Card>

            <p className="text-sm text-gray-600 text-center">
              If any information is incorrect, please go back and update it before proceeding.
            </p>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg text-blue-800">Consent for Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer p-4 bg-white border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.consent_identity_verification}
                    onChange={(e) => handleInputChange("consent_identity_verification", e.target.checked)}
                    className="w-5 h-5 mt-0.5"
                  />
                  <div>
                    <span className="font-medium text-sm">Identity Verification Consent *</span>
                    <p className="text-xs text-gray-600 mt-1">
                      I consent to Tzur Finance (Pvt) Ltd verifying my identity through relevant government 
                      databases and third-party identity verification services. I understand this is necessary 
                      for loan processing and fraud prevention.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-4 bg-white border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.consent_credit_bureau}
                    onChange={(e) => handleInputChange("consent_credit_bureau", e.target.checked)}
                    className="w-5 h-5 mt-0.5"
                  />
                  <div>
                    <span className="font-medium text-sm">Credit Bureau Check Consent *</span>
                    <p className="text-xs text-gray-600 mt-1">
                      I authorize Tzur Finance (Pvt) Ltd to conduct a credit bureau check and obtain my 
                      credit report from relevant credit bureaus. I understand this information will be 
                      used to assess my creditworthiness and loan eligibility.
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>

            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Declaration & Authorization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-700">
                <p>
                  I hereby confirm that all information provided in this application is true and correct 
                  to the best of my knowledge. I understand that any false statement may result in 
                  immediate denial of this loan request.
                </p>
                <p>
                  I authorize Tzur Finance (Pvt) Ltd to obtain any relevant information required for 
                  loan processing, including inquiries from banks, financial bureaus, suppliers, and 
                  other creditors. I release these institutions from liability under any applicable law.
                </p>
              </CardContent>
            </Card>

            <div>
              <Label>Application Fee (USD)</Label>
              <Input
                type="number"
                value={formData.application_fee}
                onChange={(e) => handleInputChange("application_fee", e.target.value)}
                placeholder="Non-refundable application fee"
                className="mt-2"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.declaration_agreed}
                onChange={(e) => handleInputChange("declaration_agreed", e.target.checked)}
                className="w-5 h-5 mt-0.5"
              />
              <span className="text-sm">
                I have read, understood, and agree to the above declarations. I confirm all information 
                is accurate and authorize Tzur Finance to process my application. *
              </span>
            </label>

            {(!formData.consent_identity_verification || !formData.consent_credit_bureau || !formData.declaration_agreed) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Please provide all required consents and agree to the declaration to submit your application.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A2540] mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your Sol Prop Loan application has been received.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Application ID: <span className="font-mono font-semibold">{applicationId}</span>
            </p>
            <p className="text-sm text-gray-600 mb-8">
              We will review your application and contact you within 24-48 hours.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        );
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <CardTitle>Login Required</CardTitle>
            <CardDescription>Please sign in to apply for an Individual Loan</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Link to="/login">
              <Button className="w-full">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="w-full">Create Account</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            <Link to="/individual-loans">
              <Button variant="ghost" className="text-gray-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Individual Loans
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {step <= totalSteps && (
        <div className="bg-white border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Step {step} of {totalSteps}</span>
              <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#0A2540] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step <= totalSteps && (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0A2540]/10 mb-4">
              <StepIcon className="h-7 w-7 text-[#0A2540]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0A2540]">{getStepTitle()}</h2>
            <p className="text-gray-500 mt-1">Sol Prop Loan Application</p>
          </div>
        )}

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {renderStep()}
          </CardContent>
        </Card>

        {step <= totalSteps && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button onClick={handleNext} className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading || !formData.declaration_agreed}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Submit Application
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default IndividualLoanApplication;
